import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Dialog as Modal } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiClient } from '../lib/api-client';
import { useToast } from '../hooks/useToast';

interface User {
  id: string;
  email: string;
  role: string;
  created_at: number;
  api_keys_count?: number;
  last_active?: number;
}

export const UsersPage: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'member',
  });

  const { success, error: showError } = useToast();

  useEffect(() => {
    loadUsers();
  }, [page, search, roleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response: any = await apiClient.getUsers({
        page,
        limit: 20,
        search: search || undefined,
        role: roleFilter || undefined,
      });

      setUsers(response.users || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (err: any) {
      showError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await apiClient.createUser(formData);
      success('User created successfully');
      setShowCreateModal(false);
      setFormData({ email: '', password: '', role: 'member' });
      loadUsers();
    } catch (err: any) {
      showError(err.message || 'Failed to create user');
    }
  };

  const handleEdit = async () => {
    if (!selectedUser) return;

    try {
      await apiClient.updateUser(selectedUser.id, {
        email: formData.email,
        role: formData.role,
      });
      success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      showError(err.message || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await apiClient.deleteUser(selectedUser.id);
      success('User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      showError(err.message || 'Failed to delete user');
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      role: user.role,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };



  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('users.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('users.subtitle')}
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            {t('users.inviteUser')}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder={t('users.searchByEmail')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: '', label: t('users.allRoles') },
                { value: 'owner', label: t('users.roles.owner') },
                { value: 'admin', label: t('users.roles.admin') },
                { value: 'member', label: t('users.roles.member') },
                { value: 'viewer', label: t('users.roles.viewer') },
              ]}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.email')}</TableHead>
                <TableHead>{t('users.status')}</TableHead>
                <TableHead>{t('users.role')}</TableHead>
                <TableHead>{t('users.apiKeys')}</TableHead>
                <TableHead>{t('users.created')}</TableHead>
                <TableHead>{t('logs.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    {t('users.loading')}
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    {t('users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const isOnline = user.last_active && (Math.floor(Date.now() / 1000) - user.last_active < 300);
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{user.email}</div>
                          <div className="text-xs text-muted-foreground">ID: {user.id.substring(0, 8)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`h-2.5 w-2.5 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                          <span className="text-sm text-muted-foreground">
                            {isOnline ? t('users.active') : t('users.offline')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            user.role === 'owner' ? 'destructive' :
                              user.role === 'admin' ? 'default' :
                                'secondary'
                          }
                          className={
                            user.role === 'owner' ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' :
                              user.role === 'admin' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' :
                                'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                          }
                        >
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{user.api_keys_count || 0}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {new Date(user.created_at * 1000).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(user)}
                          >
                            {t('common:actions.edit')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => openDeleteModal(user)}
                          >
                            {t('common:actions.delete')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />

        {/* Create Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title={t('users.inviteNewUser')}
        >
          <div className="space-y-4">
            <Input
              label={t('users.email')}
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
            <Input
              label={t('users.temporaryPassword')}
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required
              helperText={t('users.passwordHelper')}
            />
            <Select
              label={t('users.role')}
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              options={[
                { value: 'admin', label: t('users.roles.admin') },
                { value: 'member', label: t('users.roles.member') },
                { value: 'viewer', label: t('users.roles.viewer') },
              ]}
            />
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
              >
                {t('users.cancel')}
              </Button>
              <Button onClick={handleCreate}>{t('users.sendInvitation')}</Button>
            </div>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title={t('users.editUser')}
        >
          <div className="space-y-4">
            <Input
              label={t('users.email')}
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
            <Select
              label={t('users.role')}
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              options={[
                { value: 'owner', label: t('users.roles.owner') },
                { value: 'admin', label: t('users.roles.admin') },
                { value: 'member', label: t('users.roles.member') },
                { value: 'viewer', label: t('users.roles.viewer') },
              ]}
            />
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowEditModal(false)}
              >
                {t('users.cancel')}
              </Button>
              <Button onClick={handleEdit}>{t('users.saveChanges')}</Button>
            </div>
          </div>
        </Modal>

        {/* Delete Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={t('users.deleteUser')}
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('users.confirmDelete')}{' '}
              <strong>{selectedUser?.email}</strong>{t('users.cannotUndo')}
            </p>
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                {t('users.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                {t('users.deleteUser')}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AppShell>
  );
};
